/**
 * The right-hand panel.
 *
 * One payload today — a meeting opened from a citation — but it is a panel
 * rather than a route because the point of citing is that the answer and its
 * evidence are on screen together. Navigating to the meeting would mean losing
 * the conversation that sent you there.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export type PanelPayload = {
  type: 'meeting'
  meetingId: string
  /**
   * The sentence whose citation was clicked. Kept so the panel can say what the
   * meeting is being shown to support, rather than opening a wall of notes and
   * leaving the reader to work out which one was meant.
   */
  claim?: string
  /**
   * Where in the meeting the citation points. A transcript second is also a
   * video second, so this addresses the recording as well as the text.
   */
  atSeconds?: number
  /** The cited turns, carried so the panel shows them without a second fetch. */
  turns?: Array<{
    turnId: number
    speaker: string
    text: string
    startSeconds: number | null
    context?: boolean
  }>
}

const STORAGE_KEY = 'nohup.notes.panelWidth'
const MIN_WIDTH = 360
const MAX_WIDTH_RATIO = 0.72

interface PanelValue {
  payload: PanelPayload | null
  width: number
  open: (payload: PanelPayload) => void
  close: () => void
  setWidth: (width: number) => void
  isOpen: boolean
}

const PanelContext = createContext<PanelValue | null>(null)

function readWidth(): number {
  try {
    const stored = Number.parseInt(localStorage.getItem(STORAGE_KEY) ?? '', 10)
    if (Number.isFinite(stored) && stored >= MIN_WIDTH) return stored
  } catch {
    /* private mode */
  }
  return 520
}

export function PanelProvider({ children }: { children: React.ReactNode }) {
  const [payload, setPayload] = useState<PanelPayload | null>(null)
  const [width, setWidthState] = useState(readWidth)

  const setWidth = useCallback((next: number) => {
    const max = Math.max(MIN_WIDTH, window.innerWidth * MAX_WIDTH_RATIO)
    const clamped = Math.min(Math.max(next, MIN_WIDTH), max)
    setWidthState(clamped)
    try {
      localStorage.setItem(STORAGE_KEY, String(Math.round(clamped)))
    } catch {
      /* private mode */
    }
  }, [])

  const close = useCallback(() => setPayload(null), [])

  useEffect(() => {
    if (!payload) return
    const onKey = (event: KeyboardEvent) => {
      // Only when nothing is being typed into — Escape in the composer or a
      // note editor must not yank the panel out from under the user.
      const target = event.target as HTMLElement | null
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true
      if (event.key === 'Escape' && !typing) close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [payload, close])

  const value = useMemo<PanelValue>(
    () => ({ payload, width, open: setPayload, close, setWidth, isOpen: payload !== null }),
    [payload, width, close, setWidth]
  )

  return <PanelContext.Provider value={value}>{children}</PanelContext.Provider>
}

export function usePanel(): PanelValue {
  const value = useContext(PanelContext)
  if (!value) throw new Error('usePanel must be used inside <PanelProvider>')
  return value
}

/** The drag handle. Pointer events, so it works with a trackpad and a pen. */
export function PanelResizer() {
  const { width, setWidth } = usePanel()
  const [dragging, setDragging] = useState(false)

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') setWidth(width + 24)
        if (event.key === 'ArrowRight') setWidth(width - 24)
      }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId)
        setDragging(true)
      }}
      onPointerMove={(event) => {
        if (!dragging) return
        setWidth(window.innerWidth - event.clientX)
      }}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId)
        setDragging(false)
      }}
      className={
        'relative w-px shrink-0 cursor-col-resize bg-[var(--color-border)] outline-none ' +
        'after:absolute after:inset-y-0 after:-left-1.5 after:w-3 after:content-[""] ' +
        'hover:bg-[var(--color-primary)] focus-visible:bg-[var(--color-primary)] ' +
        (dragging ? 'bg-[var(--color-primary)]' : '')
      }
    />
  )
}
